/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { DividerNew, Heading } from 'sarsaparilla';
import { memo, useMemo } from 'react';
import CommonFilter from '../common/CommonFilter';

import {
    CAMPING_TYPES,
    ELECTRIC_TYPE,
    VEHICLE_LENGTH_TYPE,
} from '../../../constants/filters/camping';

import { CampingTypeFiltersProps, FiltersTypeProps } from '../../../types/filters';
import { getCount, getGroupCount } from '../../../utils/filters';
import NumberFieldFilter from '../common/NumberFieldFilter';

function FiltersComponent({
    search,
    onApplyFilters,
    onChange,
    aggregations,
    filterEmptyFilters,
    clickTagCategory,
    onCriteriaUpdate,
    maxVehicleLength,
    baselineAggregations,
}: CampingTypeFiltersProps) {
    const hasDivider = filterEmptyFilters || false;
    const dividerClassName = !filterEmptyFilters ? 'pt-2' : '';

    const filterByBaseline = useMemo(
        () => (filters: FiltersTypeProps[]) => {
            if (!baselineAggregations || Object.keys(baselineAggregations).length === 0) {
                return filters;
            }
            return filters.filter((item) => baselineAggregations?.[item.key] > 0);
        },
        [baselineAggregations]
    );

    return (
        <>
            <CommonFilter
                aggregations={aggregations}
                search={search}
                filters={filterByBaseline(CAMPING_TYPES)}
                title="Camping Type"
                onChange={onChange}
                filterEmptyFilters={filterEmptyFilters}
                hasDivider={false}
                clickTagCategory={clickTagCategory}
            />
            <div className={dividerClassName}>
                <DividerNew />
            </div>
            <div className="pt-2">
                <NumberFieldFilter
                    label={VEHICLE_LENGTH_TYPE.value}
                    id={VEHICLE_LENGTH_TYPE.key}
                    units="feet"
                    max={maxVehicleLength || 99}
                    onApplyFilters={onApplyFilters}
                    filter={VEHICLE_LENGTH_TYPE.key}
                    unitAfter="ft"
                    aggregations={aggregations}
                    filterEmptyFilters={filterEmptyFilters}
                    onCriteriaUpdate={onCriteriaUpdate}
                    search={search}
                />
            </div>
            <CommonFilter
                aggregations={aggregations}
                search={search}
                filters={filterByBaseline(ELECTRIC_TYPE)}
                filterEmptyFilters={filterEmptyFilters}
                title="Electricity"
                onChange={onChange}
                hasDivider={hasDivider}
                clickTagCategory={clickTagCategory}
            />
        </>
    );
}

export const Filters = memo(FiltersComponent, (prevProps, nextProps) => {
    return (
        prevProps.aggregations === nextProps.aggregations &&
        prevProps.search === nextProps.search &&
        prevProps.onChange === nextProps.onChange &&
        prevProps.onApplyFilters === nextProps.onApplyFilters &&
        prevProps.maxVehicleLength === nextProps.maxVehicleLength &&
        prevProps.baselineAggregations === nextProps.baselineAggregations
    );
});

function CampsiteType({
    search,
    onApplyFilters,
    filterEmptyFilters,
    onChange,
    aggregations,
    clickTagCategory,
    onCriteriaUpdate,
}: CampingTypeFiltersProps) {
    const vehicleLengthCount = getCount(['campsiteVehicleLength'], search?.uiFilters);
    const filterKeys = [...CAMPING_TYPES, ...ELECTRIC_TYPE, VEHICLE_LENGTH_TYPE].map(
        (item) => item.key
    );
    const campingCount = vehicleLengthCount + getGroupCount(filterKeys, search);
    return (
        <div>
            <div className="search-filters-section-header rounded-layout mb-1 mt-4">
                <Heading headingLevel={2} appearance="h5">
                    Select the Camping filters that work for you.
                </Heading>
                <span>
                    {campingCount}{' '}
                    {campingCount > 1 || campingCount === 0 ? 'Filters' : 'Filter'}{' '}
                    Selected
                </span>
            </div>
            <div className="search-filters-filter-container">
                <Filters
                    aggregations={aggregations}
                    search={search}
                    onApplyFilters={onApplyFilters}
                    onChange={onChange}
                    filterEmptyFilters={filterEmptyFilters}
                    clickTagCategory={clickTagCategory}
                    onCriteriaUpdate={onCriteriaUpdate}
                />
            </div>
        </div>
    );
}

export default CampsiteType;
